import {DropdownAsync} from "components/Dropdown/DropdownAsync";
import {calculateSectionSubtotal, createEmptyProductItem} from "utils/invoiceMapper";

function formatCurrency(value) {
	return new Intl.NumberFormat("id-ID", {
		style: "currency",
		currency: "IDR",
		maximumFractionDigits: 0,
	}).format(Number(value) || 0);
}

export function InvoiceProductSection({
	title,
	relationFields = [],
	relationFieldAutocompletes = {},
	titleAutocomplete,
	idField,
	idLabel,
	items = [],
	errorItems = [],
	onChange,
}) {
	let normalizedRelationFields = relationFields;

	if (!normalizedRelationFields?.length && idField) {
		normalizedRelationFields = [{key: idField, label: idLabel || idField}];
	}

	const updateItem = (index, key, value) => {
		const isRelationField = normalizedRelationFields.some(relationField => relationField.key === key);
		const sanitizedValue = isRelationField && typeof value === "string" ? value.trim() : value;
		const updatedItems = [...items];

		updatedItems[index] = {
			...updatedItems[index],
			[key]: sanitizedValue,
		};

		onChange(updatedItems);
	};

	const addItem = () => {
		onChange([...items, createEmptyProductItem(normalizedRelationFields)]);
	};

	const removeItem = index => {
		onChange(items.filter((_, itemIndex) => itemIndex !== index));
	};

	return (
		<div className="box-white ws p24 mb-3">
			<div className="d-flex justify-content-between align-items-center mb-3">
				<div className="ttl-20">{title}</div>
				<div className="d-flex align-items-center">
					{/* {titleAutocomplete?.onRefresh ? (
						<button
							className="btn btn-outline-secondary mr-2"
							disabled={titleAutocomplete?.isRefreshing}
							type="button"
							onClick={titleAutocomplete.onRefresh}>
							{titleAutocomplete?.isRefreshing ? "Memuat..." : "Refresh Options"}
						</button>
					) : null} */}
					<button className="btn btn-orange" type="button" onClick={addItem}>
						Tambah Item
					</button>
				</div>
			</div>

			{!items.length ? <div className="text-muted mb-3">Belum ada item</div> : null}

			{items.map((item, index) => (
				<div key={`${title}-${index}`} className="border rounded p-3 mb-3">
					<div className="row g-2">
						<div className="col-md-4">
							<label className="setting-label">Title</label>
							{titleAutocomplete ? (
								<DropdownAsync
									key={`${title}-${index}-${item?.id || ""}-${item?.title || ""}`}
									isClearable
									defaultOptions={(() => {
										const configuredDefaultOptions = titleAutocomplete?.defaultOptions || [];
										const selectedOption = titleAutocomplete?.getOptionFromItem
											? titleAutocomplete.getOptionFromItem(item)
											: null;

										if (!selectedOption?.value) {
											return configuredDefaultOptions;
										}

										return [
											selectedOption,
											...configuredDefaultOptions.filter(
												option => option?.value !== selectedOption?.value,
											),
										];
									})()}
									defaultValue={
										titleAutocomplete?.getOptionFromItem
											? titleAutocomplete.getOptionFromItem(item)
											: null
									}
									disabled={titleAutocomplete?.disabled}
									fetchOptions={titleAutocomplete.fetchOptions}
									isLoading={titleAutocomplete?.isLoading}
									label={titleAutocomplete?.label || "Cari title..."}
									loadingMessage={titleAutocomplete?.loadingMessage}
									noOptionsMessage={titleAutocomplete?.noOptionsMessage}
									onChange={async option => {
										const mappedChanges = titleAutocomplete?.mapOptionToItem
											? await Promise.resolve(titleAutocomplete.mapOptionToItem(option, item))
											: {
													title: option?.label || "",
											  };
										const updatedItems = [...items];

										updatedItems[index] = {
											...updatedItems[index],
											...mappedChanges,
										};

										onChange(updatedItems);
									}}
								/>
							) : (
								<input
									className="form-control"
									type="text"
									value={item?.title || ""}
									onChange={event => updateItem(index, "title", event.target.value)}
								/>
							)}
						</div>
						<div className="col-md-2">
							<label className="setting-label">Qty</label>
							<input
								className="form-control"
								min="0"
								type="number"
								value={item?.qty || ""}
								onChange={event => updateItem(index, "qty", event.target.value)}
							/>
							{errorItems?.[index]?.qty ? (
								<p className="form-error-item-message">{errorItems[index].qty}</p>
							) : null}
						</div>
						<div className="col-md-3">
							<label className="setting-label">Price</label>
							<input
								className="form-control"
								min="0"
								type="number"
								value={item?.price || ""}
								onChange={event => updateItem(index, "price", event.target.value)}
							/>
							{errorItems?.[index]?.price ? (
								<p className="form-error-item-message">{errorItems[index].price}</p>
							) : null}
						</div>
						{normalizedRelationFields.map(relationField => {
							const relationAutocomplete = relationFieldAutocompletes?.[relationField.key];
							const selectedOption = relationAutocomplete?.getOptionFromItem
								? relationAutocomplete.getOptionFromItem(item, index, items)
								: null;
							const configuredDefaultOptions =
								typeof relationAutocomplete?.defaultOptions === "function"
									? relationAutocomplete.defaultOptions(item, index, items)
									: relationAutocomplete?.defaultOptions || [];
							const defaultOptions = selectedOption?.value
								? [
										selectedOption,
										...configuredDefaultOptions.filter(
											option => option?.value !== selectedOption?.value,
										),
								  ]
								: configuredDefaultOptions;

							return (
								<div key={`${title}-${index}-${relationField.key}`} className="col-md-3">
									<label className="setting-label">
										{relationField.label}
										{relationField?.nullable ? " (nullable)" : ""}
									</label>
									{relationAutocomplete ? (
										<DropdownAsync
											key={`${title}-${index}-${relationField.key}-${
												item?.[relationField.key] || ""
											}-${item?.medicineId || ""}`}
											defaultOptions={defaultOptions}
											defaultValue={selectedOption}
											disabled={
												typeof relationAutocomplete?.disabled === "function"
													? relationAutocomplete.disabled(item, index, items)
													: relationAutocomplete?.disabled
											}
											fetchOptions={(inputValue, callback) =>
												relationAutocomplete.fetchOptions(
													inputValue,
													callback,
													item,
													index,
													items,
												)
											}
											isClearable={relationField?.nullable}
											isLoading={
												typeof relationAutocomplete?.isLoading === "function"
													? relationAutocomplete.isLoading(item, index, items)
													: relationAutocomplete?.isLoading
											}
											label={relationAutocomplete?.label || `Cari ${relationField.label}...`}
											loadingMessage={relationAutocomplete?.loadingMessage}
											noOptionsMessage={relationAutocomplete?.noOptionsMessage}
											onChange={async option => {
												const mappedChanges = relationAutocomplete?.mapOptionToItem
													? await Promise.resolve(
															relationAutocomplete.mapOptionToItem(
																option,
																item,
																index,
																items,
															),
													  )
													: {
															[relationField.key]: option?.value || "",
													  };
												const updatedItems = [...items];

												updatedItems[index] = {
													...updatedItems[index],
													...mappedChanges,
												};

												onChange(updatedItems);
											}}
										/>
									) : (
										<input
											className="form-control"
											type="text"
											value={item?.[relationField.key] || ""}
											onChange={event => updateItem(index, relationField.key, event.target.value)}
										/>
									)}
									{errorItems?.[index]?.[relationField.key] ? (
										<p className="form-error-item-message">
											{errorItems[index][relationField.key]}
										</p>
									) : null}
								</div>
							);
						})}
					</div>

					<div className="d-flex justify-content-between mt-3">
						<div>
							Subtotal item: {formatCurrency((Number(item?.qty) || 0) * (Number(item?.price) || 0))}
						</div>
						<button className="btn btn-outline-danger" type="button" onClick={() => removeItem(index)}>
							Hapus
						</button>
					</div>
				</div>
			))}

			<div className="text-bold">
				Subtotal {title}: {formatCurrency(calculateSectionSubtotal(items))}
			</div>
		</div>
	);
}
